import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Loader2, Plus, ArrowUp, ArrowDown, EyeOff, RotateCcw, Save, X,
  Sparkles, Check,
} from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { adminApi } from '../../lib/adminApi';
import { api, formatApiError } from '../../lib/api';

/**
 * Inline customization editor for a single client fork.
 * Renders the fork's questions grouped by section. Each question shows:
 *   - prompt (ES) + prompt_en (EN) editable inputs
 *   - up/down reorder
 *   - hide / restore toggle (sets is_active)
 * Plus a per-section "Add question" button (creates a basic Likert item).
 *
 * Saving uses adminApi.patchVersionQuestion / addVersionQuestion /
 * reorderVersionQuestions. Canonical versions are immutable so we never
 * land here unless `version.client_id` is set.
 */
export function CustomizationDialog({ open, onClose, questionnaire, onVersionChanged, preselectCompanyId }) {
  const { t, i18n } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(null);   // the active fork
  const [diff, setDiff] = useState(null);          // Iter 41.14 — per-Q diff
  const [effectiveness, setEffectiveness] = useState(null); // Iter 41.14 — Effectiveness card
  const [dirty, setDirty] = useState({});         // questionId → patch
  // Iter 41.7 — AI suggestions panel
  const [suggestions, setSuggestions] = useState(null); // null = not yet loaded
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  // Honour preselect — opens the dialog already pointed at a client.
  useEffect(() => {
    if (open && preselectCompanyId) {
      setCompanyId(preselectCompanyId);
      setShowEditor(true);  // skip the suggestions wall — they've decided to edit
    }
  }, [open, preselectCompanyId]);

  // Load companies list when dialog opens
  useEffect(() => {
    if (!open) return;
    let live = true;
    (async () => {
      try {
        const data = await adminApi.listCompanies();
        if (!live) return;
        setCompanies(data || []);
      } catch (e) {
        toast.error(t('toasts.loadFailed'), { description: formatApiError(e) });
      }
    })();
    return () => { live = false; };
  }, [open, t]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setCompanyId('');
      setVersion(null);
      setDirty({});
      setSuggestions(null);
      setShowEditor(false);
      fetchedKeyRef.current = null;
    }
  }, [open]);

  // When companyId changes, try to load existing fork
  useEffect(() => {
    if (!open || !companyId || !questionnaire?.id) return;
    let live = true;
    (async () => {
      try {
        const v = await adminApi.getClientVersion(questionnaire.id, companyId);
        if (live) { setVersion(v); setDirty({}); }
      } catch {
        if (live) { setVersion(null); setDiff(null); setEffectiveness(null); }
      }
    })();
    return () => { live = false; };
  }, [companyId, questionnaire?.id, open]);

  // Iter 41.14 — fetch diff + effectiveness whenever the active fork changes.
  useEffect(() => {
    if (!version?.id) { setDiff(null); setEffectiveness(null); return; }
    let live = true;
    (async () => {
      try {
        const d = await api.get(`/admin/questionnaire-versions/${version.id}/diff`).then((r) => r.data);
        if (live) setDiff(d);
      } catch { if (live) setDiff(null); }
      try {
        const e = await api.get(`/admin/questionnaire-versions/${version.id}/effectiveness`).then((r) => r.data);
        if (live) setEffectiveness(e);
      } catch { if (live) setEffectiveness(null); }
    })();
    return () => { live = false; };
  }, [version?.id, version?.questions?.length]);

  const company = companies.find((c) => c.id === companyId);
  const sortedSections = useMemo(() => {
    if (!version) return [];
    return [...(version.sections || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [version]);
  const questionsBySection = useMemo(() => {
    if (!version) return {};
    const map = {};
    for (const q of (version.questions || [])) {
      (map[q.section_id] ||= []).push(q);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return map;
  }, [version]);

  // -------- actions --------------------------------------------------------

  const createFork = async () => {
    if (!companyId) return;
    setBusy(true);
    try {
      const v = await adminApi.forkQuestionnaireForClient(questionnaire.id, companyId);
      setVersion(v);
      setDirty({});
      onVersionChanged?.(v);
      toast.success(t('common.qcustom.saved'));
    } catch (e) {
      toast.error(t('common.qcustom.saveFailed'), { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const stageEdit = (qid, patch) => {
    setDirty((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...patch } }));
  };

  const swapOrder = async (sectionId, qid, dir) => {
    const list = questionsBySection[sectionId] || [];
    const i = list.findIndex((q) => q.id === qid);
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= list.length) return;
    // optimistically swap orders in-memory
    const swapped = [...list];
    [swapped[i], swapped[j]] = [swapped[j], swapped[i]];
    const ids = swapped.map((q) => q.id);
    // Apply orders to fork copy locally + persist server-side.
    setVersion((v) => {
      const next = { ...v, questions: [...v.questions] };
      ids.forEach((id, idx) => {
        const k = next.questions.findIndex((q) => q.id === id);
        if (k >= 0) next.questions[k] = { ...next.questions[k], order: idx };
      });
      return next;
    });
    try {
      await adminApi.reorderVersionQuestions(version.id, ids);
    } catch (e) {
      toast.error(t('common.qcustom.saveFailed'), { description: formatApiError(e) });
    }
  };

  const toggleHidden = async (q) => {
    const nextActive = !(q.is_active !== false);
    try {
      const updated = await adminApi.patchVersionQuestion(version.id, q.id, { is_active: !nextActive });
      setVersion((v) => ({ ...v, questions: v.questions.map((x) => x.id === q.id ? { ...x, ...updated } : x) }));
    } catch (e) {
      toast.error(t('common.qcustom.saveFailed'), { description: formatApiError(e) });
    }
  };

  const addQuestion = async (sectionId) => {
    const promptEs = window.prompt(t('common.qcustom.newQuestionPrompt'));
    if (!promptEs) return;
    const promptEn = window.prompt(t('common.qcustom.newQuestionPromptEn')) || '';
    const baseOptions = [1, 2, 3, 4, 5].map((n) => ({ label: String(n), label_en: String(n), score: n }));
    try {
      const created = await adminApi.addVersionQuestion(version.id, {
        section_id: sectionId,
        prompt: promptEs,
        prompt_en: promptEn || promptEs,
        type: 'likert_5',
        options: baseOptions,
      });
      setVersion((v) => ({ ...v, questions: [...v.questions, created] }));
      toast.success(t('common.qcustom.saved'));
    } catch (e) {
      toast.error(t('common.qcustom.saveFailed'), { description: formatApiError(e) });
    }
  };

  const saveAllEdits = async () => {
    if (!version || Object.keys(dirty).length === 0) return;
    setBusy(true);
    try {
      for (const [qid, patch] of Object.entries(dirty)) {
        const updated = await adminApi.patchVersionQuestion(version.id, qid, patch);
        setVersion((v) => ({ ...v, questions: v.questions.map((x) => x.id === qid ? { ...x, ...updated } : x) }));
      }
      setDirty({});
      toast.success(t('common.qcustom.saved'));
    } catch (e) {
      toast.error(t('common.qcustom.saveFailed'), { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  // ----- AI suggestions ----------------------------------------------------

  // Auto-load suggestions for this (questionnaire, company) once a version
  // exists. Returns cached on every call after the first per (q, company).
  // A ref guard prevents React StrictMode's double-invoke from issuing two
  // overlapping requests.
  const fetchedKeyRef = useRef(null);
  useEffect(() => {
    if (!version || !companyId) return;
    const key = `${questionnaire?.id}::${companyId}`;
    if (fetchedKeyRef.current === key) return;
    fetchedKeyRef.current = key;
    let live = true;
    setSuggestLoading(true);
    (async () => {
      try {
        const locale = (i18n.language || 'es').startsWith('en') ? 'en' : 'es';
        const data = await adminApi.suggestCustomizations(questionnaire.id, companyId, locale);
        if (live) setSuggestions(data?.suggestions || []);
      } catch (e) {
        if (live) setSuggestions([]);  // graceful: just hide the panel
      } finally {
        if (live) setSuggestLoading(false);
      }
    })();
    return () => { live = false; };
  }, [version, companyId, questionnaire?.id, i18n.language]);

  const handleAcceptSuggestion = async (s) => {
    try {
      const updated = await adminApi.acceptSuggestion(s.id);
      setSuggestions((arr) => arr.map((x) => x.id === s.id ? updated : x));
      // Refresh version so the editor reflects the new prompt.
      const fresh = await adminApi.getClientVersion(questionnaire.id, companyId);
      setVersion(fresh);
      toast.success(t('common.qcustom.suggestStatusAccepted'));
    } catch (e) {
      toast.error(t('common.qcustom.saveFailed'), { description: formatApiError(e) });
    }
  };

  const handleRejectSuggestion = async (s) => {
    try {
      const updated = await adminApi.rejectSuggestion(s.id);
      setSuggestions((arr) => arr.map((x) => x.id === s.id ? updated : x));
    } catch (e) {
      toast.error(t('common.qcustom.saveFailed'), { description: formatApiError(e) });
    }
  };

  // Whether the suggestions panel should be shown ahead of the editor.
  const hasUndecided = (suggestions || []).some((s) => s.status === 'proposed');
  const panelVisible = version && !showEditor && (suggestLoading || hasUndecided || (suggestions && suggestions.length > 0));

  // -------- render ---------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] flex flex-col"
        data-testid="qcustom-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="qcustom-title">
            {version
              ? t('common.qcustom.editorTitle', { client: company?.name || '—' })
              : t('common.qcustom.customizeForClient')}
          </DialogTitle>
          <DialogDescription>
            {version ? t('common.qcustom.editorSubtitle') : t('common.qcustom.selectClientHint')}
          </DialogDescription>
        </DialogHeader>

        {!version && (
          <div className="space-y-4 py-2">
            <label className="text-sm font-medium">{t('common.qcustom.selectClient')}</label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger data-testid="qcustom-client-picker"><SelectValue placeholder={t('common.qcustom.selectClient')} /></SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`qcustom-client-opt-${c.id}`}>
                    {c.name}
                  </SelectItem>
                ))}
                {companies.length === 0 && (
                  <div className="p-3 text-sm text-muted-foreground">{t('common.qcustom.noClients')}</div>
                )}
              </SelectContent>
            </Select>
            <Button
              onClick={createFork}
              disabled={!companyId || busy}
              data-testid="qcustom-create-btn"
              className="bg-gradient-primary gap-1.5"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? t('common.qcustom.creating') : t('common.qcustom.create')}
            </Button>
          </div>
        )}

        {panelVisible && (
          <ScrollArea className="flex-1 -mx-2 px-2" data-testid="qcustom-suggest-panel">
            <div className="space-y-3 py-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-base font-bold flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    {t('common.qcustom.suggestPanelTitle')}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('common.qcustom.suggestPanelSubtitle')}
                  </p>
                </div>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setShowEditor(true)}
                  data-testid="qcustom-skip-suggestions"
                >
                  {t('common.qcustom.suggestOpenEditor')}
                </Button>
              </div>

              {suggestLoading && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-6 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('common.qcustom.suggestGenerating')}
                </div>
              )}

              {!suggestLoading && suggestions && suggestions.length === 0 && (
                <div className="text-xs text-muted-foreground py-6 text-center">
                  {t('common.qcustom.suggestEmpty')}
                </div>
              )}

              {!suggestLoading && (suggestions || []).map((s) => {
                const decided = s.status !== 'proposed';
                return (
                  <Card
                    key={s.id}
                    className={`p-4 space-y-2 ${decided ? 'opacity-60' : ''}`}
                    data-testid={`qcustom-suggest-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <Badge variant="secondary" className="text-xs uppercase">
                        {t(`common.qcustom.suggestKind${s.kind.charAt(0).toUpperCase() + s.kind.slice(1)}`, { defaultValue: s.kind })}
                      </Badge>
                      {decided && (
                        <Badge variant="outline" className="text-xs">
                          {s.status === 'accepted'
                            ? t('common.qcustom.suggestStatusAccepted')
                            : t('common.qcustom.suggestStatusRejected')}
                        </Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">
                        {t('common.qcustom.suggestNewVersion')}
                      </p>
                      <p className="text-sm font-medium text-foreground">{s.new_prompt}</p>
                      {s.new_prompt_en && s.new_prompt_en !== s.new_prompt && (
                        <p className="text-xs text-muted-foreground italic mt-1">{s.new_prompt_en}</p>
                      )}
                    </div>
                    {s.rationale && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">
                          {t('common.qcustom.suggestRationale')}
                        </p>
                        <p className="text-xs text-muted-foreground">{s.rationale}</p>
                      </div>
                    )}
                    {!decided && (
                      <div className="flex items-center gap-2 pt-1">
                        <Button size="sm"
                                onClick={() => handleAcceptSuggestion(s)}
                                data-testid={`qcustom-suggest-accept-${s.id}`}
                                className="bg-gradient-primary gap-1.5">
                          <Check className="h-3.5 w-3.5" /> {t('common.qcustom.suggestAccept')}
                        </Button>
                        <Button size="sm" variant="outline"
                                onClick={() => handleRejectSuggestion(s)}
                                data-testid={`qcustom-suggest-reject-${s.id}`}
                                className="gap-1.5">
                          <X className="h-3.5 w-3.5" /> {t('common.qcustom.suggestReject')}
                        </Button>
                      </div>
                    )}
                  </Card>
                );
              })}

              <p className="text-xs text-muted-foreground italic">
                {t('common.qcustom.suggestNoCallsLeft')}
              </p>
            </div>
          </ScrollArea>
        )}

        {version && !panelVisible && (
          <ScrollArea className="flex-1 -mx-2 px-2">
            <div className="space-y-6 py-2">
              {/* Iter 41.14 — Effectiveness Check card */}
              {diff?.summary && (
                <Card className="p-3 flex items-center gap-2 flex-wrap" data-testid="qcustom-diff-summary">
                  <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    {t('common.qcustom.diff.summary')}:
                  </span>
                  <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30">
                    {t('common.qcustom.diff.modified')} · {diff.summary.modified}
                  </Badge>
                  <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/30">
                    {t('common.qcustom.diff.added')} · {diff.summary.added}
                  </Badge>
                  <Badge variant="outline" className="text-xs bg-muted text-muted-foreground">
                    {t('common.qcustom.diff.hidden')} · {diff.summary.hidden}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {t('common.qcustom.diff.unchanged')} · {diff.summary.unchanged}
                  </Badge>
                </Card>
              )}
              {effectiveness && (
                <Card className="p-4 space-y-3 border-primary/30 bg-primary/[0.03]" data-testid="qcustom-effectiveness">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display font-bold text-sm">{t('common.qcustom.effectiveness.title')}</h4>
                    <Badge variant="outline" className="text-xs">
                      {effectiveness.responses_completed}/{effectiveness.min_required}
                    </Badge>
                  </div>
                  {!effectiveness.ready ? (
                    <p className="text-xs text-muted-foreground italic" data-testid="qcustom-effectiveness-locked">
                      {t('common.qcustom.effectiveness.locked', { n: effectiveness.min_required })}
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="p-2 rounded-md bg-card border">
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('common.qcustom.effectiveness.completionLabel')}</p>
                          <p className="text-base font-bold tabular-nums">{effectiveness.custom?.completion_pct}%</p>
                          {effectiveness.canonical && (
                            <p className="text-xs text-muted-foreground">vs {effectiveness.canonical.completion_pct}% {t('common.qcustom.effectiveness.canonicalShort')}</p>
                          )}
                        </div>
                        <div className="p-2 rounded-md bg-card border">
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('common.qcustom.effectiveness.lengthLabel')}</p>
                          <p className="text-base font-bold tabular-nums">{effectiveness.custom?.avg_open_text_length}</p>
                          {effectiveness.canonical && (
                            <p className="text-xs text-muted-foreground">vs {effectiveness.canonical.avg_open_text_length} {t('common.qcustom.effectiveness.canonicalShort')}</p>
                          )}
                        </div>
                      </div>
                      <ul className="space-y-1" data-testid="qcustom-effectiveness-signals">
                        {(effectiveness.signals || []).map((sig) => (
                          <li key={sig} className="text-xs px-2 py-1.5 rounded-md bg-card border">
                            {t(`common.qcustom.effectiveness.signal.${sig}`)}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </Card>
              )}
              {sortedSections.map((sec) => {
                const items = questionsBySection[sec.id] || [];
                return (
                  <Card key={sec.id} className="p-4 space-y-3" data-testid={`qcustom-section-${sec.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-display text-base font-bold">{sec.name}</h3>
                        {sec.name_en && sec.name_en !== sec.name && (
                          <p className="text-xs text-muted-foreground">{sec.name_en}</p>
                        )}
                      </div>
                      <Button size="sm" variant="outline" className="gap-1.5"
                              onClick={() => addQuestion(sec.id)}
                              data-testid={`qcustom-add-q-${sec.id}`}>
                        <Plus className="h-3.5 w-3.5" /> {t('common.qcustom.addQuestion')}
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {items.map((q, idx) => {
                        const hidden = q.is_active === false;
                        const diffItem = (diff?.items || []).find((it) => it.question_id === q.id);
                        const dStatus = diffItem?.status || 'unchanged';
                        const dTone = {
                          modified: 'bg-warning/10 text-warning border-warning/30',
                          hidden: 'bg-muted text-muted-foreground border-border',
                          added: 'bg-success/10 text-success border-success/30',
                          unchanged: '',
                        }[dStatus];
                        const dBorder = {
                          modified: 'border-l-4 border-l-warning',
                          hidden: 'border-l-4 border-l-muted-foreground/40',
                          added: 'border-l-4 border-l-success',
                          unchanged: '',
                        }[dStatus];
                        return (
                          <div
                            key={q.id}
                            className={`border rounded-lg p-3 space-y-2 ${dBorder} ${hidden ? 'opacity-50' : ''}`}
                            data-testid={`qcustom-q-${q.id}`}
                          >
                            {dStatus !== 'unchanged' && (
                              <Badge
                                variant="outline"
                                className={`text-xs uppercase tracking-wider ${dTone}`}
                                data-testid={`qcustom-diff-badge-${q.id}`}
                              >
                                {t(`common.qcustom.diff.${dStatus}`)}
                              </Badge>
                            )}
                            {dStatus === 'modified' && diffItem?.original && (
                              <details className="text-xs" data-testid={`qcustom-diff-orig-${q.id}`}>
                                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                  {t('common.qcustom.diff.viewOriginal')}
                                </summary>
                                <div className="mt-1 p-2 rounded bg-muted/40 border space-y-1">
                                  <p><span className="text-xs uppercase tracking-wider text-muted-foreground">ES:</span> {diffItem.original.prompt}</p>
                                  {diffItem.original.prompt_en && (
                                    <p><span className="text-xs uppercase tracking-wider text-muted-foreground">EN:</span> {diffItem.original.prompt_en}</p>
                                  )}
                                </div>
                              </details>
                            )}
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-mono text-muted-foreground mt-2 w-6 shrink-0">
                                {idx + 1}
                              </span>
                              <div className="flex-1 space-y-2">
                                <div>
                                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                                    {t('common.qcustom.promptEs')}
                                  </label>
                                  <Textarea
                                    rows={2}
                                    value={dirty[q.id]?.prompt ?? q.prompt}
                                    onChange={(e) => stageEdit(q.id, { prompt: e.target.value })}
                                    data-testid={`qcustom-prompt-es-${q.id}`}
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                                    {t('common.qcustom.promptEn')}
                                  </label>
                                  <Textarea
                                    rows={2}
                                    value={dirty[q.id]?.prompt_en ?? (q.prompt_en || '')}
                                    onChange={(e) => stageEdit(q.id, { prompt_en: e.target.value })}
                                    data-testid={`qcustom-prompt-en-${q.id}`}
                                    className="text-sm"
                                  />
                                </div>
                                {hidden && (
                                  <Badge variant="secondary" className="text-xs">
                                    {t('common.qcustom.hidden')}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex flex-col gap-1 shrink-0">
                                <Button size="icon" variant="ghost" className="h-7 w-7"
                                  disabled={idx === 0}
                                  onClick={() => swapOrder(sec.id, q.id, 'up')}
                                  data-testid={`qcustom-up-${q.id}`}
                                  aria-label={t('common.qcustom.moveUp')}>
                                  <ArrowUp className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7"
                                  disabled={idx === items.length - 1}
                                  onClick={() => swapOrder(sec.id, q.id, 'down')}
                                  data-testid={`qcustom-down-${q.id}`}
                                  aria-label={t('common.qcustom.moveDown')}>
                                  <ArrowDown className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7"
                                  onClick={() => toggleHidden(q)}
                                  data-testid={`qcustom-hide-${q.id}`}
                                  aria-label={hidden ? t('common.qcustom.showQuestion') : t('common.qcustom.hideQuestion')}>
                                  {hidden
                                    ? <RotateCcw className="h-3.5 w-3.5" />
                                    : <EyeOff className="h-3.5 w-3.5" />}
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="gap-1.5">
            <X className="h-4 w-4" />
            {t('common.cancel')}
          </Button>
          {version && (
            <Button onClick={saveAllEdits} disabled={busy || Object.keys(dirty).length === 0}
                    className="bg-gradient-primary gap-1.5"
                    data-testid="qcustom-save-all">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {busy ? t('common.qcustom.saving') : t('common.qcustom.saveChanges')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CustomizationDialog;
